import { Component, Host, h, Prop, Event, Method, Listen, Watch, Element } from '@stencil/core';
import { floatingElements, VIEWPORT_ATTRIBUTE } from '../../utils/floating-elements.utils';
const dialogsOpened = new Array();
/**
 * @slot  - Dialog content.
 * @slot title - Use to add dialog header title. Only works if property dialogTitle is undefined.
 */
export class Dialog {
  constructor() {
    /**
     * Internal boolean to indicate whether dialog is visible.
     * Used to identify when the property `visible` was modified externally.
     */
    this.isVisibleInternal = false;
    /**
     * Defines the dialog size.
     */
    this.dialogSize = 'medium';
    /**
     * Sets the position of the footer buttons.
     */
    this.footerButtonsPosition = 'start';
    /**
     * Whether true, the backdrop will not be displayed.
     */
    this.hideBackdrop = false;
    /**
     * Whether true, the dialog close button will not be displayed.
     */
    this.hideClose = false;
    /**
     * Dialog position style.
     * Whether fixed, the dialog will be positioned in the center of the viewport and the backdrop will fill the viewport.
     * Whether absolute, the dialog will be positioned relative to the nearest positioned ancestor and the backdrop will fill the space of the nearest positioned ancestor.
     */
    this.position = 'fixed';
    /**
     * Whether true, the dialog header will not be displayed.
     */
    this.hideHeader = false;
    /**
     * Whether true, the dialog footer will not be displayed.
     */
    this.hideFooter = false;
    /**
     * Whether true, the dropdown will not close with an outside click.
     */
    this.ignoreOutsideClick = false;
    /**
     * Defines the label of the dialog submit button.
     */
    this.submitLabel = 'Save';
    /**
     * Defines the label of the dialog cancel button.
     */
    this.cancelLabel = 'Cancel';
    /**
     * Defines if the dialog is visible
     */
    this.visible = false;
  }
  visibleWatcher() {
    if (this.isVisibleInternal === this.visible)
      return;
    if (this.visible) {
      this.open();
    }
    else {
      this.close();
    }
  }
  /**
   * Method responsible for closing the dialog. Can receive the default closing action identifiers "submit" and "cancel", or a custom closing action identifier.
   */
  async close(action) {
    if (this.isVisibleInternal) {
      const shouldProceed = await this.onClosing(action);
      if (shouldProceed === false)
        return;
      this.onClose();
      this.onClosed(action);
    }
  }
  /**
   * Method responsible for opening the dialog.
   */
  async open() {
    if (!this.isVisibleInternal) {
      const shouldProceed = await this.onOpening();
      if (shouldProceed === false)
        return;
      this.onOpen();
      this.onOpened();
    }
  }
  /**
   * Get dialog internal elements reference.
   */
  async getReferences() {
    return {
      backdropElement: this.backdropEl,
    };
  }
  onSyDialogHeaderDidCloseClick(event) {
    if (event) {
      this.close('cancel');
    }
  }
  onSyDialogFooterDidCancelClick(event) {
    if (event) {
      this.close('cancel');
    }
  }
  onSyDialogFooterDidSubmitClick(event) {
    if (event) {
      this.close('submit');
    }
  }
  onKeyDown(ev) {
    if (ev.key === 'Escape') {
      const lastOpened = dialogsOpened.at(dialogsOpened.length - 1);
      if (lastOpened === this.el) {
        this.close('cancel');
        ev.stopImmediatePropagation();
      }
    }
  }
  componentDidLoad() {
    this.visibleWatcher();
  }
  async onOpening() {
    const beforeOpenedResult = this.beforeOpened && (await this.beforeOpened());
    if (beforeOpenedResult === false) {
      return beforeOpenedResult;
    }
    this.isVisibleInternal = true;
    this.visible = true;
  }
  onOpen() {
    floatingElements.open(this.el);
  }
  async onOpened() {
    this.syDialogDidOpen.emit();
    if (this.afterOpened) {
      await this.afterOpened();
    }
    dialogsOpened.push(this.el);
  }
  async onClosing(action) {
    const beforeClosedResult = this.beforeClosed && (await this.beforeClosed(action));
    if (beforeClosedResult === false) {
      return beforeClosedResult;
    }
    this.isVisibleInternal = false;
    this.visible = false;
  }
  onClose() {
    floatingElements.close(this.el);
  }
  async onClosed(action) {
    if (action === 'cancel')
      this.syDialogDidCancel.emit();
    if (action === 'submit')
      this.syDialogDidSubmit.emit();
    this.syDialogDidClose.emit(action);
    if (this.afterClosed) {
      await this.afterClosed(action);
    }
    const thisDialogIndex = dialogsOpened.findIndex(el => el === this.el);
    dialogsOpened.splice(thisDialogIndex, 1);
  }
  handleOutsideClick(event, outsideEl) {
    if (!this.ignoreOutsideClick && event.target == outsideEl) {
      event.stopPropagation();
      this.close('cancel');
    }
  }
  render() {
    return (h(Host, Object.assign({}, { [VIEWPORT_ATTRIBUTE]: true }),
      h("sy-backdrop", { ref: el => (this.backdropEl = el), onClick: e => this.handleOutsideClick(e, this.backdropEl) },
        h("sy-focus-trap", null,
          h("div", { class: "sy-dialog" },
            !this.hideHeader && (h("sy-dialog-header", { dialogTitle: this.dialogTitle, hideCloseButton: this.hideClose },
              h("slot", { name: "title" }))),
            h("sy-dialog-content", null,
              h("slot", null)),
            !this.hideFooter && (h("sy-dialog-footer", { cancelLabel: this.cancelLabel, submitLabel: this.submitLabel, style: {
                flexDirection: this.footerButtonsPosition === 'start' ? 'row' : 'row-reverse',
              } })))))));
  }
  static get is() { return "sy-dialog"; }
  static get encapsulation() { return "shadow"; }
  static get originalStyleUrls() { return {
    "$": ["scss/index.scss", "scss/_tokens.scss"]
  }; }
  static get styleUrls() { return {
    "$": ["./scss/index.css", "./scss/_tokens.css"]
  }; }
  static get properties() { return {
    "afterClosed": {
      "type": "unknown",
      "mutable": false,
      "complexType": {
        "original": "(action?: string) => void | Promise<void>",
        "resolved": "(action?: string) => void | Promise<void>",
        "references": {
          "Promise": {
            "location": "global"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Callback function called after the dialog is closed."
      }
    },
    "afterOpened": {
      "type": "unknown",
      "mutable": false,
      "complexType": {
        "original": "() => void | Promise<void>",
        "resolved": "() => void | Promise<void>",
        "references": {
          "Promise": {
            "location": "global"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Callback function called after the dialog is opened."
      }
    },
    "beforeClosed": {
      "type": "unknown",
      "mutable": false,
      "complexType": {
        "original": "(action?: string) => void | boolean | Promise<void | boolean>",
        "resolved": "(action?: string) => boolean | void | Promise<boolean | void>",
        "references": {
          "Promise": {
            "location": "global"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Callback function called before the dialog is closed.\nIf returns 'false', the dialog will not be closed."
      }
    },
    "beforeOpened": {
      "type": "unknown",
      "mutable": false,
      "complexType": {
        "original": "() => void | boolean | Promise<void | boolean>",
        "resolved": "() => boolean | void | Promise<boolean | void>",
        "references": {
          "Promise": {
            "location": "global"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Callback function called before the dialog is opened.\nIf returns 'false', the dialog will not be opened."
      }
    },
    "dialogSize": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "'small' | 'medium' | 'large' | 'extra-large' | 'full'",
        "resolved": "\"extra-large\" | \"full\" | \"large\" | \"medium\" | \"small\"",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Defines the dialog size."
      },
      "attribute": "dialog-size",
      "reflect": true,
      "defaultValue": "'medium'"
    },
    "dialogTitle": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Title."
      },
      "attribute": "dialog-title",
      "reflect": false
    },
    "footerButtonsPosition": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "'start' | 'end'",
        "resolved": "\"end\" | \"start\"",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Sets the position of the footer buttons."
      },
      "attribute": "footer-buttons-position",
      "reflect": true,
      "defaultValue": "'start'"
    },
    "hideBackdrop": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Whether true, the backdrop will not be displayed."
      },
      "attribute": "hide-backdrop",
      "reflect": true,
      "defaultValue": "false"
    },
    "hideClose": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Whether true, the dialog close button will not be displayed."
      },
      "attribute": "hide-close",
      "reflect": true,
      "defaultValue": "false"
    },
    "position": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "'fixed' | 'absolute'",
        "resolved": "\"absolute\" | \"fixed\"",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Dialog position style.\nWhether fixed, the dialog will be positioned in the center of the viewport and the backdrop will fill the viewport.\nWhether absolute, the dialog will be positioned relative to the nearest positioned ancestor and the backdrop will fill the space of the nearest positioned ancestor."
      },
      "attribute": "position",
      "reflect": true,
      "defaultValue": "'fixed'"
    },
    "hideHeader": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Whether true, the dialog header will not be displayed."
      },
      "attribute": "hide-header",
      "reflect": true,
      "defaultValue": "false"
    },
    "hideFooter": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Whether true, the dialog footer will not be displayed."
      },
      "attribute": "hide-footer",
      "reflect": true,
      "defaultValue": "false"
    },
    "ignoreOutsideClick": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Whether true, the dropdown will not close with an outside click."
      },
      "attribute": "ignore-outside-click",
      "reflect": true,
      "defaultValue": "false"
    },
    "submitLabel": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Defines the label of the dialog submit button."
      },
      "attribute": "submit-label",
      "reflect": true,
      "defaultValue": "'Save'"
    },
    "cancelLabel": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Defines the label of the dialog cancel button."
      },
      "attribute": "cancel-label",
      "reflect": true,
      "defaultValue": "'Cancel'"
    },
    "visible": {
      "type": "boolean",
      "mutable": true,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Defines if the dialog is visible"
      },
      "attribute": "visible",
      "reflect": true,
      "defaultValue": "false"
    }
  }; }
  static get events() { return [{
      "method": "syDialogDidOpen",
      "name": "syDialogDidOpen",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": "Emitted after dialog opening."
      },
      "complexType": {
        "original": "void",
        "resolved": "void",
        "references": {}
      }
    }, {
      "method": "syDialogDidClose",
      "name": "syDialogDidClose",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": "Emitted after dialog closing. Contains by default the closing action identifiers \"submit\" and \"cancel\".\nWhen the dialog is closed by the close method with a custom closing action identifier, the event emits the custom close action identifier."
      },
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      }
    }, {
      "method": "syDialogDidSubmit",
      "name": "syDialogDidSubmit",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": "Emitted after dialog submit."
      },
      "complexType": {
        "original": "void",
        "resolved": "void",
        "references": {}
      }
    }, {
      "method": "syDialogDidCancel",
      "name": "syDialogDidCancel",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": "Emitted after dialog cancel."
      },
      "complexType": {
        "original": "void",
        "resolved": "void",
        "references": {}
      }
    }]; }
  static get methods() { return {
    "close": {
      "complexType": {
        "signature": "(action?: 'submit' | 'cancel' | string) => Promise<void>",
        "parameters": [{
            "tags": [],
            "text": ""
          }],
        "references": {
          "Promise": {
            "location": "global"
          }
        },
        "return": "Promise<void>"
      },
      "docs": {
        "text": "Method responsible for closing the dialog. Can receive the default closing action identifiers \"submit\" and \"cancel\", or a custom closing action identifier.",
        "tags": []
      }
    },
    "open": {
      "complexType": {
        "signature": "() => Promise<void>",
        "parameters": [],
        "references": {
          "Promise": {
            "location": "global"
          }
        },
        "return": "Promise<void>"
      },
      "docs": {
        "text": "Method responsible for opening the dialog.",
        "tags": []
      }
    },
    "getReferences": {
      "complexType": {
        "signature": "() => Promise<{ [name: string]: HTMLElement; }>",
        "parameters": [],
        "references": {
          "Promise": {
            "location": "global"
          },
          "HTMLElement": {
            "location": "global"
          }
        },
        "return": "Promise<{ [name: string]: HTMLElement; }>"
      },
      "docs": {
        "text": "Get dialog internal elements reference.",
        "tags": []
      }
    }
  }; }
  static get elementRef() { return "el"; }
  static get watchers() { return [{
      "propName": "visible",
      "methodName": "visibleWatcher"
    }]; }
  static get listeners() { return [{
      "name": "syDialogHeaderDidCloseClick",
      "method": "onSyDialogHeaderDidCloseClick",
      "target": undefined,
      "capture": false,
      "passive": false
    }, {
      "name": "syDialogFooterDidCancelClick",
      "method": "onSyDialogFooterDidCancelClick",
      "target": undefined,
      "capture": false,
      "passive": false
    }, {
      "name": "syDialogFooterDidSubmitClick",
      "method": "onSyDialogFooterDidSubmitClick",
      "target": undefined,
      "capture": false,
      "passive": false
    }, {
      "name": "keydown",
      "method": "onKeyDown",
      "target": "document",
      "capture": false,
      "passive": false
    }]; }
}
